import React from 'react';
import styled from 'styled-components';
import {
  getHtmlFromMarkdown,
  parseJSONOrDefault,
  prefixObjectValues,
  enums,
} from '../../../utils';
import { convertAnchorTagsToRouterLinks } from '../../../utils/html-parsing-helpers';
import { models } from '../../../utils/cms-client';
import * as options from '../options';
import { defaults, getFont } from '../simple-styled-components';
import { rem } from '../../../style';
import { FormContext } from '../../../contexts/form-context';
import useHandlebars from '../../../hooks/use-handlebars';
import { RefreshContext } from '../../../contexts/refresh-context';

interface ComponentProps {
  keyId?: string;
  text: models.FormText;
  formValueGetter: (name: string) => any;
  moveToStepCall?: Function;
}

const domIdsStatic = {
  rootNode: 'form-text',
};

export const domIdsUnique = (prefix?: string) =>
  prefixObjectValues(prefix, domIdsStatic);

const Container = styled.div`
  margin: 1.5rem 0rem;
  font-family: ${getFont(defaults.fontName, defaults.fontType).fontFamily};
  font-weight: ${getFont(defaults.fontName, defaults.fontType).fontWeight};
  font-size: ${rem(defaults.fontSize)};
  color: ${defaults.color};
`;

const FormText: (p: ComponentProps) => React.ReactElement | null = ({
  keyId,
  text,
  formValueGetter,
  moveToStepCall,
}) => {
  const domIds = domIdsUnique(keyId);
  const { formContext } = React.useContext(FormContext);
  const { refreshDetails } = React.useContext(RefreshContext);
  const markdown = useHandlebars(
    text?.elements?.markdown_text?.value,
    {
      ...formContext,
    },
    formValueGetter,
  );
  const [optionsTemplate, setOptionsTemplate] = React.useState({});

  React.useEffect(() => {
    setOptionsTemplate(
      parseJSONOrDefault(text?.elements?.options_template?.value),
    );
  }, [text]);

  const isVisible = options.shouldShow(
    optionsTemplate,
    formValueGetter,
    formContext,
    refreshDetails,
  );

  // Check if this element has the blockProgress flag
  const hasBlockProgressFlag = !!text?.elements?.flags?.value?.filter(
    (f) => f.codename === enums.FormFlags.blockProgress,
  )?.length;

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = (e.target as HTMLElement).closest('a[href^="moveToStep:"]');
    if (!target || !moveToStepCall) return;
    e.preventDefault();
    const [, stepCodename, fieldName] =
      target.getAttribute('href')?.split(':') ?? [];
    if (stepCodename) {
      moveToStepCall(undefined, fieldName, stepCodename);
    }
  };

  return isVisible ? (
    <Container
      id={domIds.rootNode}
      data-block-progress={hasBlockProgressFlag ? 'true' : 'false'}
      onClick={handleClick}
    >
      {convertAnchorTagsToRouterLinks(getHtmlFromMarkdown(markdown).__html)}
    </Container>
  ) : null;
};

export default FormText;
